/**
 * The tenant lease details: one list, used by the screen that edits them and
 * by every screen that shows them.
 *
 * These were ten rows in the Task table, created from standardTasks.js when a
 * tenant project was opened and edited one dialog at a time - a date entered
 * as a task "due date", a yes/no answer entered as a checkbox on a task called
 * "Tenant Paying Rent?". They are now columns on the project, filled in from
 * the Lease Details tab in one save. Migration 010 added the columns and moved
 * what the old tasks held.
 *
 * Four of them were already project columns and stay where they are:
 * lease_effective_date, rent_commencement_date, plans_due_date (the TI plans)
 * and deadline_to_open, plus ti_allowance. Nothing is duplicated - a second
 * column for the same date is how two dates for the same thing start
 * disagreeing.
 *
 * `onReport` marks the fields the tenant update report carries, so a Property
 * Manager can check a tenant against the lease without opening the project.
 */

export const TENANT_LEASE_SECTIONS = [
  {
    key: 'lease',
    title: 'Lease',
    description: 'The dates the lease itself sets, and anything else worth noting about it.',
    fields: [
      { key: 'lease_execution_date', label: 'Lease Execution Date', type: 'date',
        help: 'Fully executed lease received by all parties' },
      { key: 'lease_effective_date', label: 'Lease Effective Date', type: 'date', onReport: true },
      { key: 'lease_commencement_date', label: 'Lease Commencement Date', type: 'date', onReport: true },
      /* Yes, No or N/A first, and the date only once it is a yes. "No" (there
         are contingencies, not yet satisfied) and "N/A" (the lease has none)
         are complete answers with no date to give. Changing a yes to anything
         else hides the date rather than clearing it, the same as the
         landlord's work description.

         Stored as text in contingencies_status (migration 014), limited by
         CK_project_contingencies_status to exactly these three values - keep
         them in step with `enums` in api/src/lib/entities.ts. 013's bit column,
         contingencies_satisfied, had no room for a third answer; 014 copied
         its answers across and nothing reads it any more. */
      { key: 'contingencies_status', label: 'Contingencies Satisfied', type: 'choice',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A' },
        ],
        describes: 'contingencies_satisfied_date',
        help: 'All lease contingencies and conditions precedent satisfied' },
      { key: 'contingencies_satisfied_date', label: 'Date Contingencies Satisfied', type: 'date',
        dependsOn: 'contingencies_status' },
      /* The lease terms that are not a date - renewal options, exclusives,
         co-tenancy and the like. `optional` because most leases have nothing
         to add here: it is not counted toward the five day window, and the
         reminder sweep never asks for it (the API's copy of this list says
         the same). It is on the Tenant Updates report, but as its own line
         rather than through `onReport`, which is the strip of short values
         beside the dates - see LEASE_NOTES_KEY below. Migration 016. */
      { key: 'lease_notes', label: 'Additional Lease Notes', type: 'text', optional: true,
        placeholder: 'Any lease terms not tracked by a date - renewal options, exclusives, co-tenancy…' },
    ],
  },
  {
    key: 'onboarding',
    title: 'Tenant onboarding',
    description: 'Getting the tenant into the space.',
    fields: [
      { key: 'welcome_letter_sent_date', label: 'Welcome Letter Sent', type: 'date',
        help: 'Tenant welcome package and key contacts sent' },
      { key: 'space_delivered_date', label: 'Space Delivered', type: 'date',
        help: 'Premises delivered per lease terms (condition and possession)' },
      { key: 'tenant_insurance_date', label: 'Certificate of Tenant Insurance', type: 'date',
        help: 'Certificate received and approved per lease requirements' },
    ],
  },
  {
    key: 'buildout',
    title: 'Build-out',
    description: 'The improvement work and what it is worth.',
    fields: [
      { key: 'plans_due_date', label: 'TI Plans Due', type: 'date', onReport: true },
      { key: 'ti_allowance', label: 'TI Amount', type: 'money', onReport: true },
      /* What the TI work has actually cost so far, against the TI Amount
         above (migration 019). The same column is the actual spend on a
         capital or MOE project, where it sits beside the budget instead.
         `optional`: nobody knows it inside the five day window, and it keeps
         moving until the project closes, so it is never chased. */
      { key: 'actual_spend', label: 'Actual Spend', type: 'money', onReport: true, optional: true,
        help: 'TI money actually spent so far - the reports compare it with the TI Amount' },
      /* TI drawdown (migration 030): a lease that lets the TI be paid in
         several draws. A yes swaps the single payment's two dates below for
         a list of draws - each its own amount, typed by hand, its expected
         date and its paid date (Evan, 2026-11-04). The single dates are
         hidden, not cleared, so switching back loses nothing; the draws are
         kept the same way. Both `optional`: a blank switch means the single
         payment, and draws are paid long after the five day window. The
         editor is TiDrawsEditor; the rules are in tiDraws.js. */
      { key: 'ti_drawdown', label: 'TI Drawdown', type: 'yesno', optional: true,
        describes: 'ti_draws',
        help: 'Yes if the lease lets the TI be paid in several draws' },
      { key: 'ti_draws', label: 'TI Draws', type: 'draws', optional: true, dependsOn: 'ti_drawdown' },
      { key: 'ti_payment_expected_date', label: 'TI Payment Expected Date', type: 'date',
        hiddenWhen: 'ti_drawdown',
        help: 'When the TI allowance is expected to be paid to the tenant' },
      { key: 'ti_payment_actual_date', label: 'TI Payment Made', type: 'date',
        hiddenWhen: 'ti_drawdown',
        help: 'The date the TI payment was actually made' },
      { key: 'signage_due_date', label: 'Signage Due Date', type: 'date', onReport: true,
        help: 'Deadline for tenant signage design submission' },
      { key: 'build_out_start_date', label: 'Build-out Period Start', type: 'date' },
      { key: 'build_out_end_date', label: 'Build-out Period End', type: 'date' },
      { key: 'landlord_work', label: "Landlord's Work", type: 'yesno',
        describes: 'landlord_work_description' },
      { key: 'landlord_work_description', label: "Describe the landlord's work", type: 'text',
        dependsOn: 'landlord_work',
        placeholder: 'What the landlord is responsible for delivering…' },
    ],
  },
  {
    key: 'opening',
    title: 'Opening and rent',
    description: 'What the lease requires, and what actually happened.',
    fields: [
      { key: 'deadline_to_open', label: 'Deadline to Open Operations', type: 'date', onReport: true },
      { key: 'open_date', label: 'Open Date', type: 'date',
        help: 'The date the tenant actually opened for business' },
      { key: 'rent_commencement_date', label: 'Rent Commencement Date', type: 'date', onReport: true },
      /* On the report as Yes or No, and left off while it is unanswered - see
         reportValue in tenantUpdatesPdf.js (Evan, 2026-10-20). */
      { key: 'tenant_paying_rent', label: 'Tenant Paying Rent', type: 'yesno', onReport: true },
    ],
  },
];

export const TENANT_LEASE_FIELDS = TENANT_LEASE_SECTIONS.flatMap(s => s.fields);

/** The fields the tenant update report carries, in the order it shows them. */
export const TENANT_REPORT_FIELDS = TENANT_LEASE_FIELDS.filter(f => f.onReport);

/**
 * The free-text lease notes, which the Tenant Updates report carries as a line
 * of their own under the dates - on the card, in the PDF and as a CSV column.
 */
export const LEASE_NOTES_KEY = 'lease_notes';
export const LEASE_NOTES_LABEL = TENANT_LEASE_FIELDS.find(f => f.key === LEASE_NOTES_KEY).label;

/**
 * How long the Property Manager has to fill the screen in, from the day the
 * Project Manager opens the project.
 *
 * Five days is the agreed window. It is stored on the project as
 * `tenant_details_due_date` rather than derived on the fly, so that it can be
 * moved when there is a reason to - and it is manager-only in the API, with no
 * tenant release, so the person it is set for is not the person who moves it.
 */
export const TENANT_DETAILS_WINDOW_DAYS = 5;

/** The deadline for a project opened on `from`, as a yyyy-mm-dd string. */
export function tenantDetailsDueDate(from = new Date()) {
  const d = new Date(from);
  d.setDate(d.getDate() + TENANT_DETAILS_WINDOW_DAYS);
  return toDateInput(d);
}

/**
 * A date as `<input type="date">` wants it: yyyy-mm-dd, local.
 *
 * SQL `date` columns come back through the driver as midnight UTC, so slicing
 * the ISO string is right for a stored value and wrong for a JS Date west of
 * Greenwich - `new Date()` at 4pm Pacific is already tomorrow in UTC. Hence
 * the two branches.
 */
export function toDateInput(value) {
  if (!value) return '';
  if (value instanceof Date) {
    const pad = n => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  return String(value).slice(0, 10);
}

/**
 * A stored date as a person reads it - "Nov 1, 2026".
 *
 * Built from the yyyy-mm-dd parts rather than by handing the whole ISO string
 * to `new Date()`, which would read midnight UTC and render the day before
 * everywhere west of Greenwich. A lease date that shows a day early is worse
 * than no date at all.
 */
export function formatDay(value, { short = false } = {}) {
  const iso = toDateInput(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', short
    ? { month: 'numeric', day: 'numeric', year: '2-digit' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Whole days from today (or `from`) to `value`; negative once it is past. */
export function daysUntil(value, from = new Date()) {
  const iso = toDateInput(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const today = new Date(from);
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

/** Is this field answered? A "no" is an answer; a blank is not. */
export function isAnswered(project, field) {
  const value = project?.[field.key];
  if (field.type === 'yesno') return value === true || value === false;
  if (field.type === 'choice') return field.options.some(o => o.value === value);
  if (field.type === 'money') return value !== null && value !== undefined && value !== '';
  return !!value;
}

/**
 * Is the answer a field depends on a yes? A yes/no stores true; a choice
 * stores the text 'yes'. Both mean the follow-up question is asked.
 */
export function isYes(value) {
  return value === true || value === 'yes';
}

/**
 * Is this field asked for on this project? Not when its `dependsOn` answer is
 * anything but yes, and not when its `hiddenWhen` answer is a yes - the single
 * TI payment dates while the TI is drawn down. `values` is wherever the
 * answers are held: the stored project, or the form while it is being edited.
 */
export function isAsked(values, field) {
  if (field.dependsOn && !isYes(values?.[field.dependsOn])) return false;
  if (field.hiddenWhen && isYes(values?.[field.hiddenWhen])) return false;
  return true;
}

/**
 * Where a project stands against its five day window.
 *
 * `filled` counts every field except the ones with a `dependsOn` whose answer
 * is not yes - the landlord's work description and the contingencies date.
 * Counting those would make a project with no landlord's work, or whose
 * contingencies are not yet satisfied or do not apply, permanently incomplete.
 * An `optional` field - the lease notes - is never counted, for the same
 * reason: a lease with nothing to add would never be complete. Nor is a field
 * hidden by a yes elsewhere - the single TI payment dates while the TI is
 * drawn down, when the draws stand in for them.
 */
export function tenantDetailsStatus(project, from = new Date()) {
  const counted = TENANT_LEASE_FIELDS.filter(f => !f.optional && isAsked(project, f));
  const filled = counted.filter(f => isAnswered(project, f)).length;
  const total = counted.length;
  const dueDate = project?.tenant_details_due_date || null;
  const daysLeft = dueDate ? daysUntil(dueDate, from) : null;
  return {
    filled,
    total,
    complete: total > 0 && filled === total,
    dueDate,
    daysLeft,
    overdue: daysLeft !== null && daysLeft < 0 && filled < total,
    updatedDate: project?.tenant_details_updated_date || null,
    updatedBy: project?.tenant_details_updated_by || '',
  };
}

/**
 * The build-out period is the one pair of lease dates that is a stretch of
 * time rather than a day, so the timeline draws it as a bar.
 */
const BUILD_OUT_START = 'build_out_start_date';
const BUILD_OUT_END = 'build_out_end_date';
/* A named constant, not a `{ key: '…', label:` literal: the API's
   tenant-lease-details test reads that exact pattern out of this file as the
   list of lease fields, and a literal here was read as a field called
   build_out_period that has no column - PR #92's deploy failed on it. */
const BUILD_OUT_PERIOD = 'build_out_period';

/**
 * The lease dates a tenant project's Timeline tab draws, in the order the
 * Lease Details tab lists them: `{ key, label, start, end }`, both yyyy-mm-dd,
 * equal for a single day.
 *
 * Built from TENANT_LEASE_FIELDS, so a date field added there appears on the
 * timeline without the chart changing. Blank dates are left out - Lease
 * Details is where a missing one is flagged - and so is a date that tab hides:
 * one whose `dependsOn` answer is not yes, or whose `hiddenWhen` answer is.
 * The build-out start
 * and end become one row, or a single day when only one of them is set.
 */
export function leaseTimelineItems(project) {
  if (project?.type !== 'tenant') return [];
  const items = [];
  for (const field of TENANT_LEASE_FIELDS) {
    if (field.type !== 'date' || field.key === BUILD_OUT_END) continue;
    if (!isAsked(project, field)) continue;
    if (field.key === BUILD_OUT_START) {
      const start = toDateInput(project[BUILD_OUT_START]);
      const end = toDateInput(project[BUILD_OUT_END]);
      if (start && end) {
        items.push({ key: BUILD_OUT_PERIOD, label: 'Build-out Period', start, end });
      } else if (start || end) {
        const key = start ? BUILD_OUT_START : BUILD_OUT_END;
        const label = TENANT_LEASE_FIELDS.find(f => f.key === key).label;
        items.push({ key, label, start: start || end, end: start || end });
      }
      continue;
    }
    const day = toDateInput(project[field.key]);
    if (day) items.push({ key: field.key, label: field.label, start: day, end: day });
  }
  return items;
}
